import { initContract } from "@ts-rest/core";
import { z } from "zod";
const c = initContract();
/** What a background media job is doing. Mirrors the DB enum. */
export const MediaJobKindSchema = z.enum(["thumbnail", "video_cut", "collage"]);
/** `pending`/`enqueued` are in flight; `done`/`error` are terminal. The RAW row state. */
export const MediaJobStatusSchema = z.enum(["pending", "enqueued", "done", "error"]);
/**
 * What to SHOW a person — derived server-side from `status` + `startedAt`.
 *
 * The raw status cannot answer "is it my turn yet?": `enqueued` covers both waiting in the queue and being actively
 * encoded. Deriving the phase on the server keeps that rule in one place instead of each client re-implementing it
 * (and drifting).
 */
export const MediaJobPhaseSchema = z.enum(["queued", "processing", "finished", "failed"]);
/**
 * One of the caller's background jobs (ADR-026 Phase 6).
 *
 * Video cutting runs on the worker and can take a while, so the UI needs somewhere to say "still working" and later
 * "done" — otherwise the only way to discover the result was to reload, which is exactly what the realtime layer is
 * meant to remove.
 *
 * `manualId`/`stepId` are present when the job belongs to a manual's body (a cut or an attached image), so the client
 * can both label the row usefully and know which cached query to refresh when it lands.
 *
 * **Provenance** (`manualName` / `sectionName` / `stepName`) answers the question the job list is actually asked —
 * *"where did this file come from?"*. A row saying "Cutting video" is unusable when three are queued; "Cutting video ·
 * Palette erzeugen · Palette ans Kühllager bringen" is a place you can go back to. Names are resolved LIVE from the
 * body on read, so a renamed section shows its current name, and each is nullable: a thumbnail has no step, and a step
 * deleted since the job ran leaves the name null rather than dropping the row from someone's history.
 */
export const MediaJobSchema = z.object({
    id: z.number().int(),
    kind: MediaJobKindSchema,
    status: MediaJobStatusSchema,
    /** The user-facing phase: queued → processing → finished (or failed). */
    phase: MediaJobPhaseSchema,
    /** When the worker actually picked it up; null while still queued. */
    startedAt: z.string().nullable(),
    manualId: z.number().int().nullable(),
    manualName: z.string().nullable(),
    stepId: z.number().int().nullable(),
    stepName: z.string().nullable(),
    sectionId: z.number().int().nullable(),
    sectionName: z.string().nullable(),
    /** The file the job PRODUCES — the cut video, the thumbnail. Null before it is known. */
    fileId: z.number().int().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** Set only on `error`, and deliberately short: a worker stack trace is not a user-facing message. */
    failedReason: z.string().nullable(),
});
export const mediaJobContract = c.router({
    /**
     * The caller's own background jobs — in-flight, plus a week of finished HISTORY so the panel can answer "what did I
     * cut yesterday?" and not only "what is running right now".
     *
     * Scoped to the caller by `created_by_user_id`: a background job is personal progress, not shared state, and
     * showing a colleague's queue would leak what they are working on.
     */
    listMyMediaJobs: {
        method: "GET",
        path: "/media/jobs",
        responses: {
            200: z.object({ jobs: z.array(MediaJobSchema) }),
            401: z.object({ message: z.string() }),
        },
        summary: "The caller's active and recently-finished background media jobs.",
    },
});
//# sourceMappingURL=media-job.contract.js.map